import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { track, trackLeadGenerated } from "@/lib/analytics";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const YEARS = ["2026", "2027", "2028"];
const BUDGETS = ["Under ₹1 Lakh", "₹1–3 Lakh", "₹3–5 Lakh", "₹5–10 Lakh", "₹10 Lakh+"];

// intent presets: title, subtitle, button, which fields to show, and GA4 lead metadata.
const PRESETS = {
  information_pack: { title: "Get FREE College Information Pack", sub: "Fees, courses, placements & admission steps — sent by our advisor.", btn: "Get My Free Pack", full: true, leadType: "information_pack_form", formName: "College Information Pack" },
  admission_guidance: { title: "Request Free Admission Guidance", sub: "Talk to an expert admission advisor — no charges.", btn: "Request Guidance", full: true, leadType: "admission_guidance_form", formName: "Admission Guidance" },
  apply: { title: "Apply Now", sub: "Start your application — our advisor will guide you through it.", btn: "Start Application", full: true, leadType: "apply_now_form", formName: "Apply Now" },
  callback: { title: "Request a Call Back", sub: "Share your number — we'll call you back shortly.", btn: "Request Call Back", full: false, leadType: "callback_form", formName: "Request Call Back" },
  expert_connect: { title: "Connect with an Admission Expert", sub: "AI counselling is temporarily unavailable — a human expert will call you with verified guidance.", btn: "Request Expert Call", full: true, leadType: "expert_connect_form", formName: "Expert Connect" },
};

export default function LeadCaptureModal({ open, onClose, intent = "information_pack", college }) {
  const preset = PRESETS[intent] || PRESETS.information_pack;
  const [form, setForm] = useState({ name: "", mobile: "", email: "", courseInterest: "", admissionYear: "2026", budget: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const evParams = {
    intent,
    college_id: college?.collegeId || college?.College_ID,
    college_name: college?.name || college?.College_Name,
    state: college?.state || college?.State,
    city: college?.city || college?.City,
  };
  useEffect(() => {
    if (open) track("form_start", evParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, intent]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile.trim())) {
      toast.error("Please enter your name and a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    track("form_submit", { ...evParams, course: form.courseInterest || undefined });
    try {
      const { data } = await api.post("/leads/capture", {
        ...form,
        intent,
        collegeId: college?.collegeId || college?.College_ID,
        collegeName: college?.name || college?.College_Name,
        collegeSlug: college?.slug,
        city: college?.city || college?.City,
        state: college?.state || college?.State,
        attribution: getAttribution(),
      });
      trackLeadGenerated(data, {
        ...evParams,
        course: form.courseInterest || undefined,
        lead_type: preset.leadType,
        form_name: preset.formName,
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="lead-capture-modal">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-6 shadow-2xl">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" data-testid="lead-modal-close"><X className="h-5 w-5" /></button>
        {done ? (
          <div className="py-6 text-center" data-testid="lead-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h3 className="mt-3 font-display text-xl font-bold text-slate-900">Thank you, {form.name.split(" ")[0]}!</h3>
            <p className="mt-2 text-slate-600">Your information pack is being prepared. Our admission advisor will contact you shortly{college?.name || college?.College_Name ? ` about ${college.name || college.College_Name}` : ""}.</p>
            <button onClick={onClose} className="mt-5 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-semibold text-white">Done</button>
          </div>
        ) : (
          <>
            <h3 className="font-display text-xl font-bold text-slate-900 pr-6">{preset.title}</h3>
            <p className="mt-1 text-sm text-slate-500">{preset.sub}</p>
            {(college?.name || college?.College_Name) && <p className="mt-2 text-sm font-medium text-[#0B2A6B]">{college.name || college.College_Name}</p>}
            <div className="mt-4 space-y-3">
              <input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Full Name *" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]" />
              <input data-testid="lead-mobile" value={form.mobile} onChange={set("mobile")} inputMode="numeric" maxLength={10} placeholder="Mobile Number *" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]" />
              {preset.full && (
                <>
                  <input data-testid="lead-email" value={form.email} onChange={set("email")} placeholder="Email (optional)" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]" />
                  <input data-testid="lead-course" value={form.courseInterest} onChange={set("courseInterest")} placeholder="Preferred Course (e.g. B.Tech, MBA)" className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]" />
                  <div className="grid grid-cols-2 gap-3">
                    <select data-testid="lead-year" value={form.admissionYear} onChange={set("admissionYear")} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]">
                      {YEARS.map((y) => <option key={y} value={y}>Admission {y}</option>)}
                    </select>
                    <select data-testid="lead-budget" value={form.budget} onChange={set("budget")} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-600 outline-none focus:border-[#0B2A6B]">
                      <option value="">Budget Range</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </>
              )}
              <button data-testid="lead-submit" onClick={submit} disabled={loading} className="w-full rounded-lg bg-[#0B2A6B] py-3 text-sm font-bold text-white hover:bg-[#081E4D] disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {preset.btn}
              </button>
              <p className="flex items-center justify-center gap-1 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> 100% free · No spam · Your details are safe</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
